import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Typography, Breadcrumbs, Link, Box, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import leavesImage from '../../../assets/leaves.png';
import { environment } from '../../../environments/environment.fattyPattyDev';

const SectionHeaderContainer = styled(Box)(({ theme }) => ({
  // Use environment main color as a subtle background tint (8% alpha via 8-digit hex)
  background: `${environment.mainColor}14`,
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  textAlign: 'center',
  position: 'relative',

  padding: theme.spacing(4, 2),

  // Responsive breakpoints
  [theme.breakpoints.down('md')]: {
    minHeight: '160px',
    padding: theme.spacing(3, 2),
  },
  [theme.breakpoints.down('sm')]: {
    minHeight: '140px',
    padding: theme.spacing(2, 1),
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.3,
  },
}));

const LeavesImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  left: '0px',
  top: '80%',
  transform: 'translateY(-50%)',
  width: '100px',
  height: 'auto',

  zIndex: 0,
  pointerEvents: 'none',

  // Responsive adjustments
  [theme.breakpoints.down('md')]: {
    width: '80px',
    left: '15px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '60px',
    left: '10px',
  },
  [theme.breakpoints.down('xs')]: {
    width: '40px',
    left: '5px',
  },
}));

const StyledTitle = styled(Typography)(({ theme, fontFamily }) => ({
  fontFamily: fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
  fontWeight: 700,
  color: '#172B4D',
  marginBottom: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: '2px',
  position: 'relative',
  zIndex: 1,

  // Responsive font sizes
  fontSize: '2.5rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.2rem',
    letterSpacing: '1.5px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '1.8rem',
    letterSpacing: '1px',
    marginBottom: theme.spacing(1.5),
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '1.5rem',
  },
}));

const StyledBreadcrumbs = styled(Breadcrumbs)(({ theme, fontFamily }) => ({
  fontFamily: fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
  position: 'relative',
  zIndex: 1,

  '& .MuiBreadcrumbs-separator': {
    color: '#7f8c8d',
    margin: '0 8px',
    fontSize: '1rem',
    [theme.breakpoints.down('sm')]: {
      margin: '0 4px',
      fontSize: '0.9rem',
    },
  },

  '& .MuiBreadcrumbs-li': {
    display: 'flex',
    alignItems: 'center',
  },
}));

const BreadcrumbLink = styled(Link)(({ theme, fontFamily }) => ({
  fontFamily: fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
  color: '#7f8c8d',
  textDecoration: 'none',
  fontSize: '1rem',
  fontWeight: 500,
  textTransform: 'uppercase',
  letterSpacing: '1px',
  cursor: 'pointer',
  transition: 'color 0.3s ease',

  '&:hover': {
    color: '#34495e',
    textDecoration: 'none',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    letterSpacing: '0.5px',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '0.8rem',
  },
}));

const CurrentPageText = styled(Typography)(({ theme, fontFamily }) => ({
  fontFamily: fontFamily || '"Roboto", "Helvetica", "Arial", sans-serif',
  color: '#2c3e50',
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '1px',

  [theme.breakpoints.down('sm')]: {
    fontSize: '0.9rem',
    letterSpacing: '0.5px',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '0.8rem',
  },
}));

const SectionHeader = ({
  title,
  breadcrumbs = [],
  fontFamily,
  backgroundColor,
  textColor,
  customStyles = {},
  showBreadcrumbs = true,
  titleVariant = 'h1',
  navbarHeight = '84px',
  useNavbarOffset = true,
  // New props for change button
  showChangeButton = false,
  changeButtonLabel = 'Change',
  onChangeClick,
  changeButtonContent = null, // for custom content if needed
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Calculate dynamic margin top based on navbar height
  const generateDefaultBreadcrumbs = () => {
    const pathSegments = location.pathname
      .split('/')
      .filter((segment) => segment);
    const defaultBreadcrumbs = [{ label: t('home'), path: '/' }];

    pathSegments.forEach((segment, index) => {
      const path = '/' + pathSegments.slice(0, index + 1).join('/');
      const label =
        segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ');

      if (index < pathSegments.length - 1) {
        defaultBreadcrumbs.push({ label, path });
      } else {
        defaultBreadcrumbs.push({ label, path, current: true });
      }
    });

    return defaultBreadcrumbs;
  };

  const finalBreadcrumbs =
    breadcrumbs.length > 0 ? breadcrumbs : generateDefaultBreadcrumbs();

  const handleBreadcrumbClick = (path, state) => {
    if (path && path !== location.pathname) {
      navigate(path, state ? { state } : {});
    }
  };

  return (
    <SectionHeaderContainer
      sx={{
        backgroundColor: backgroundColor,
        ...customStyles.container,
      }}
    >
      <LeavesImage src={leavesImage} alt="Decorative leaves" />

      <StyledTitle
        variant={titleVariant}
        fontFamily={fontFamily}
        sx={{
          color: textColor,
          ...customStyles.title,
        }}
      >
        {title}
      </StyledTitle>

      {showBreadcrumbs && (
        <StyledBreadcrumbs
          separator="/"
          fontFamily={fontFamily}
          sx={customStyles.breadcrumbs}
        >
          {finalBreadcrumbs.map((breadcrumb, index) => (
            <Box key={index}>
              {breadcrumb.current ? (
                <CurrentPageText
                  fontFamily={fontFamily}
                  sx={customStyles.currentPage}
                >
                  {breadcrumb.label}
                </CurrentPageText>
              ) : (
                <BreadcrumbLink
                  fontFamily={fontFamily}
                  onClick={() => {
                    if (breadcrumb.onClick) {
                      breadcrumb.onClick();
                    } else {
                      handleBreadcrumbClick(breadcrumb.path, breadcrumb.state);
                    }
                  }}
                  sx={customStyles.breadcrumbLink}
                >
                  {breadcrumb.label}
                </BreadcrumbLink>
              )}
            </Box>
          ))}
        </StyledBreadcrumbs>
      )}
      {/* Optional Change Button Row */}
      {showChangeButton && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 2,

            width: '100%',
          }}
        >
          {/* You can customize this box further to match the location box design */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
                  border: `1px solid ${environment.mainColor}`,
                  borderRadius: '32px',
                  // Slight tint using the main color (10% alpha)
                  background: `${environment.mainColor}1A`,
              px: 3,
              py: 1,
              minWidth: '350px',
              maxWidth: '600px',
              width: '100%',
              boxShadow: 'none',
            }}
          >
            {/* Left: location info or custom content */}
            {changeButtonContent ? (
              changeButtonContent
            ) : (
              <Typography
                sx={{
                  fontWeight: 700,
                  color: '#172B4D',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: '"Jost", sans-serif',
                }}
              >
                {/* Example icon placeholder */}
                <svg
                  width="25"
                  height="25"
                  viewBox="0 0 25 25"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M20.4963 11.121V19.5C20.4963 20.0304 20.2856 20.5391 19.9105 20.9142C19.5355 21.2893 19.0268 21.5 18.4963 21.5H6.50433C5.97407 21.4997 5.46562 21.2889 5.09076 20.9139C4.71591 20.5388 4.50533 20.0303 4.50533 19.5V11.121M8.00233 9.25L8.50233 3.5M8.00233 9.25C8.00233 12.152 12.5003 12.152 12.5003 9.25M8.00233 9.25C8.00233 12.426 2.84733 11.77 3.56933 9.002L4.61433 4.995C4.72601 4.56703 4.97641 4.18815 5.32635 3.91765C5.67629 3.64714 6.10603 3.50026 6.54833 3.5H18.4523C18.8946 3.50026 19.3244 3.64714 19.6743 3.91765C20.0243 4.18815 20.2747 4.56703 20.3863 4.995L21.4313 9.002C22.1533 11.771 16.9983 12.426 16.9983 9.25M12.5003 9.25V3.5M12.5003 9.25C12.5003 12.152 16.9983 12.152 16.9983 9.25M16.9983 9.25L16.4983 3.5"
                    stroke="#172B4D"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                18561 E Mainstreet Parker
              </Typography>
            )}
            {/* Right: Change button */}
            <Box>
              <Button
                component="button"
                onClick={onChangeClick}
                sx={{
                  color: environment.mainColor,
                  fontWeight: 700,
                  border: 'none',
                  background: 'none',

                  cursor: 'pointer',
                  textDecoration: 'underline',
                  fontSize: '14px',
                  ml: 2,
                  fontFamily: '"Jost", sans-serif',
                  '&:hover': {
                    textDecoration: 'none',
                    color: environment.mainColor,
                    background: 'none',
                  },
                }}
              >
                {changeButtonLabel}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </SectionHeaderContainer>
  );
};

export default SectionHeader;
